import * as React from 'react'
import { useRouter } from 'next/navigation'
import { useDashboardSlugs } from './use-dashboard-slugs'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useUserStore } from '~/app/_store/user-store'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useRepoStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/repo-store'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import {
  copyBlockToClipboard,
  pasteBlocksFromClipboard,
} from '~/hooks-data/copy-paste'
import { toast } from 'sonner'
import { ClipboardCopyIcon } from '@radix-ui/react-icons'
import { useFocus } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/blocks/list/helpers/useFocus'
import { useTabsStore } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/(editor)/explore/components/tabs/store'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { isHotkey } from 'is-hotkey'
import { NavItem } from '~/app/(dash)/[team-slug]/(repo)/[repo-slug]/components/repo-nav'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'

export const useRepoKeyboardNavigation = ({
  disabled,
  navItems,
  footerNavItems,
}: {
  disabled: boolean
  navItems: NavItem[]
  footerNavItems: NavItem[]
}) => {
  const router = useRouter()
  const currentRepoPage = useDashboardSlugs((s) => s.repoPage)
  const prevRepoPageRef = React.useRef<string | null>(null)
  const blockTreeManager = useWorkingTreeStore((s) => s.treeRef)
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const { data: repoReplicacheData } = useRepoReplicache()
  const replicache = repoReplicacheData?.replicache
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const collapseDocumentPanel = useUserPreferences(
    (s) => s.collapseDocumentPanel
  )
  const collapsePropertiesPanel = useUserPreferences(
    (s) => s.collapsePropertiesPanel
  )

  React.useEffect(() => {
    if (currentRepoPage && !prevRepoPageRef.current) {
      // initialize
      prevRepoPageRef.current = currentRepoPage
    }
  }, [currentRepoPage])

  const editModeIsEnabled = useUserPreferences((s) => s.editMode)

  const onNavigateThroughRepo = React.useCallback(
    async (e: KeyboardEvent) => {
      if (isHotkey('F1', e)) {
        e.preventDefault()
        window.open('https://help.basehub.com', '_blank')
        return
      }

      if (isHotkey('mod+b', e)) {
        if (document.activeElement?.closest('.ProseMirror')) {
          return
        }
        if (
          replicache &&
          optimisticUser &&
          collapseDocumentPanel !== undefined
        ) {
          await replicache.mutate.setCollapsedDocumentPanel({
            userId: optimisticUser.id,
            isCollapsed: !collapseDocumentPanel,
          })
        }
        return
      }
      if (isHotkey('mod+j', e)) {
        if (document.activeElement?.closest('.ProseMirror')) {
          return
        }
        if (
          replicache &&
          optimisticUser &&
          collapsePropertiesPanel !== undefined
        ) {
          await replicache.mutate.setCollapsedPropertiesPanel({
            userId: optimisticUser.id,
            isCollapsed: !collapsePropertiesPanel,
          })
        }
      }
      if (!e.ctrlKey) return

      const match = [...navItems, ...footerNavItems].find((item) => {
        if (typeof item.shortcutIndex !== 'number') return false
        return e.code === `Digit${item.shortcutIndex}`
      })

      if (match) {
        router.push(match.linkProps.href.toString())
      }
    },
    [
      collapseDocumentPanel,
      footerNavItems,
      navItems,
      optimisticUser,
      replicache,
      router,
      collapsePropertiesPanel,
    ]
  )

  const setCutBlocks = useRepoStore((s) => s.setCutBlocks)

  const onCopy = React.useCallback(
    async (e: ClipboardEvent) => {
      const focusedElement = document.activeElement
      if (!focusedElement) return
      const blockId = focusedElement.getAttribute('data-tree-list-item')
      if (!blockId) return
      // first clear the copied blocks from before
      setCutBlocks(null)

      if (!blockTreeManager.current) return
      e.preventDefault()

      const block = blockTreeManager.current?.getNode(blockId)?.value
      if (block?.type === 'root') return

      await copyBlockToClipboard({
        blockId: blockId,
        blockTreeManager: blockTreeManager.current,
        onSuccess: () => {
          toast(
            block?.title
              ? `Block "${block.title ?? 'Untitled'}" copied to clipboard`
              : 'Block copied to clipboard',
            { icon: <ClipboardCopyIcon /> }
          )
        },
        onError: (err) => {
          console.error(err)
          toast.error('An error occurred while copying block to clipboard')
        },
      })
    },
    [setCutBlocks, blockTreeManager]
  )

  const cutBlocks = useRepoStore((s) => s.cutBlocks)
  const addTab = useTabsStore((s) => s.addTab)
  const setFocus = useFocus(false)

  const onPaste = React.useCallback(
    async (e: ClipboardEvent) => {
      if (!blockTreeManager.current || !replicache) return

      const focusedElement = document.activeElement
      if (!focusedElement) return
      let selectedBlockId = focusedElement.getAttribute('data-tree-list-item')
      const isPastingOnRoot =
        focusedElement.getAttribute('data-tree-list') === 'true'
      if (!selectedBlockId) {
        if (!isPastingOnRoot) return
        selectedBlockId = blockTreeManager.current.root.key
      }
      if (!e.clipboardData) return

      e.preventDefault()

      const selectedNode = blockTreeManager.current.getNode(selectedBlockId, {
        required: true,
      })
      const destinationPath = blockTreeManager.current.getPath(selectedNode, {
        required: true,
      })

      const pasteResult = await pasteBlocksFromClipboard({
        repoId: repoReplicacheData.repoId,
        destinationPath,
        selectedBlock: null,
        blockTreeManager: blockTreeManager.current,
        editModeIsEnabled,
        replicache,
        repoStore: { cutBlocks, setCutBlocks },
        addTab,
        shiftKeyPressed: false,
      })
      if (pasteResult?.newBlock && pasteResult?.undo) {
        const focusOp = {
          op: 'focus',
          args: {
            focus: {
              blockId: idToSourceBlockId(pasteResult.newBlock),
              type: 'block:wrapper',
            },
            preferredTabKey: activeTabKey,
          },
        } as const
        undoManager.current?.push({
          redo: [pasteResult.redo, focusOp],
          undo: [
            focusOp,
            {
              op: 'blockBatchMutations',
              args: { mutations: pasteResult.undo },
            },
          ],
        })
      }

      setTimeout(() => {
        if (!blockTreeManager.current) return
        if (pasteResult?.newBlock?.id) {
          if (isPastingOnRoot) {
            addTab({ key: pasteResult.newBlock.id, partiallyOpen: true })
          } else {
            addTab({ key: selectedNode.value.id, partiallyOpen: true })
            setFocus({
              blockId: idToSourceBlockId(pasteResult.newBlock),
              type: 'block:wrapper',
            })
          }
        }
      }, 16)
    },
    [
      addTab,
      blockTreeManager,
      cutBlocks,
      editModeIsEnabled,
      activeTabKey,
      replicache,
      repoReplicacheData?.repoId,
      setCutBlocks,
      setFocus,
      undoManager,
    ]
  )

  React.useLayoutEffect(() => {
    if (disabled) return

    document.addEventListener('keydown', onNavigateThroughRepo)
    document.addEventListener('paste', onPaste)
    document.addEventListener('copy', onCopy)

    return () => {
      document.removeEventListener('keydown', onNavigateThroughRepo)
      document.removeEventListener('paste', onPaste)
      document.removeEventListener('copy', onCopy)
    }
  }, [disabled, onCopy, onNavigateThroughRepo, onPaste])

  React.useEffect(() => {
    if (currentRepoPage === prevRepoPageRef.current) return
    if (!prevRepoPageRef.current) {
      prevRepoPageRef.current = currentRepoPage
      return
    }
    // expand the document panel when navigating to a different repo page
    if (
      !repoReplicacheData?.replicache ||
      !optimisticUser ||
      !collapseDocumentPanel
    ) {
      return
    }
    repoReplicacheData.replicache.mutate
      .setCollapsedDocumentPanel({
        userId: optimisticUser.id,
        isCollapsed: false,
      })
      .then(() => {
        prevRepoPageRef.current = currentRepoPage
      })
  }, [
    collapseDocumentPanel,
    currentRepoPage,
    optimisticUser,
    repoReplicacheData?.replicache,
  ])
}
